import { Button, Font, Icon } from '@pepsico-ds/ui'
import { GsptWebsiteHeader } from 'pepsico-ds-inner-source'
import { useOktaAuth } from '@okta/okta-react'
import React from 'react'
import { useUnsavedGuard } from 'contexts/UnsavedGuard'

function AccessErrorPage() {
  const { oktaAuth } = useOktaAuth()
  const { suppressGuard } = useUnsavedGuard()
  let email = ''
  try {
    email = JSON.parse(sessionStorage.getItem('okta-token-storage'))?.idToken?.claims?.email || ''
  } catch (error) {
    // Keep the access error page renderable if the session token is unavailable or malformed.
  }

  const logout = async () => {
    // Suppress unsaved changes prompts during logout
    suppressGuard()

    try {
      const tokens = await oktaAuth.tokenManager.getTokens()
      const idToken = tokens?.idToken

      // Broadcast logout so other tabs sign out too
      try {
        localStorage.setItem('AGRO_GSAT_BROADCAST_LOGOUT', String(Date.now()))
      } catch (e) {
        // ignore storage errors
      }

      const consent = localStorage.getItem('cookieConsent')
      if (consent === 'declined') {
        try {
          sessionStorage.removeItem('cookiePopoverDismissedThisSession')
        } catch (e) {
          // ignore storage errors
        }
      }

      await oktaAuth.signOut({
        // Ensure RP-initiated logout ends the Okta session
        idToken,
        // Ask SDK to revoke tokens for you (don’t revoke manually beforehand)
        revokeAccessToken: true,
        revokeRefreshToken: true,
        // Send the user to a page that doesn't auto-trigger login
        postLogoutRedirectUri: `${window.location.origin}`,
      })

      // NOTE: In most cases the call above will redirect the browser;
      // code below may not run.
    } catch (e) {
      console.error('Logout error:', e)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: '#F5F9FF',
      }}
    >
      <GsptWebsiteHeader
        logoType="fulllogo"
        status="signedIn"
        customToolbarIcons={[]}
        avatar={null}
      />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          padding: 'var(--spacing-16)',
        }}
      >
        <main
          style={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            padding: 'var(--spacing-16)',
            backgroundColor: 'var(--color-background-layer-01, #ffffff)',
            borderRadius: 'var(--border-radius-small)',
            boxShadow: '0 4px 12px rgba(58, 58, 58, 0.08)',
          }}
        >
          <section
            aria-labelledby="access-error-title"
            style={{
              display: 'flex',
              flex: 1,
              minWidth: 0,
              justifyContent: 'center',
              alignItems: 'center',
              padding: 'var(--spacing-16)',
              backgroundColor: '#F5F9FF',
              border: '1px solid var(--color-border-default, #e5e5e5)',
              borderRadius: 'var(--border-radius-small)',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                gap: 'var(--spacing-20, 20px)',
                textAlign: 'center',
              }}
            >
              <Icon
                alt="Access denied"
                className="$color-icon-default"
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34" fill="none">
  <path d="M15 21.6667H18.3333V25H15V21.6667ZM15 8.33333H18.3333V18.3333H15V8.33333ZM16.65 0C7.45 0 0 7.46667 0 16.6667C0 25.8667 7.45 33.3333 16.65 33.3333C25.8667 33.3333 33.3333 25.8667 33.3333 16.6667C33.3333 7.46667 25.8667 0 16.65 0ZM16.6667 30C9.3 30 3.33333 24.0333 3.33333 16.6667C3.33333 9.3 9.3 3.33333 16.6667 3.33333C24.0333 3.33333 30 9.3 30 16.6667C30 24.0333 24.0333 30 16.6667 30Z" fill="#2D2D2D"/>
</svg>}
                size="extra-large"
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  gap: 'var(--spacing-20, 20px)',
                }}
              >
                <Font
                  headingLevel={1}
                  id="access-error-title"
                  style={{ color: 'var(--color-text-primary, #2d2d2d)', margin: 0, fontSize: '28px' }}
                >
                  You don&apos;t have access to the Global Seed Planning Tool
                </Font>
                <Font
                  size="body"
                  style={{
                    color: 'var(--color-text-primary, #2d2d2d)',
                    margin: 0,
                  }}
                >
                  {`You're signed in as ${email}. If you believe you should have access, contact the GSPT Team.`}
                </Font>
                <Button
                  variant="secondary"
                  size="large"
                  text="Use another account"
                  iconTrailing="arrow_forward"
                  onClick={logout}
                  style={{
                    height: '40px',
                    border: '1px solid var(--color-interaction-primary, #004895)',
                    borderRadius: 'var(--border-radius-action-rounded, 9999px)',
                    backgroundColor: 'var(--color-background-layer-01, #ffffff)',
                    color: 'var(--color-interaction-primary, #004895)',
                    fontWeight: 400,
                  }}
                />
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default AccessErrorPage
